// OIDC redirect landing (/auth/callback). Completes the PKCE code exchange, then
// hands the resolved principal to the EXISTING auth flow: useAuth().login(principal)
// persists the local session id and calls /me (the API client attaches the Bearer
// token), so all the dummy-login machinery — auth.slice, RequireAuth, the "/" gate —
// is reused unchanged. On failure we route back to /login.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, ErrorState } from '../../components/reusable';
import { useAuth } from '../AuthProvider';
import { handleSigninCallback } from './userManager';
import { OIDC_PRINCIPAL_CLAIM } from './oidcConfig';
import '../../pages/Login/login.css';

// Module-level, NOT a ref: the auth code + PKCE state are single-use, and
// signinRedirectCallback() consumes the stored state on first success — a second
// call throws "No matching state found in storage". A useRef guard doesn't survive
// StrictMode's effect re-invoke / a remount, but a module flag does. The callback
// page is a fresh full-page load from the IdP, so this resets on each real attempt.
let callbackHandled = false;

export function AuthCallback() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (callbackHandled) return;
    callbackHandled = true;
    (async () => {
      try {
        const user = await handleSigninCallback();
        // Prefer the configured claim; fall back to `sub` for the local session id
        // (the backend independently reads the participant claim from the access token).
        const principal = user?.profile?.[OIDC_PRINCIPAL_CLAIM] || user?.profile?.sub;
        if (!principal) {
          throw new Error('Sign-in succeeded but no identity claim was found in the token.');
        }
        // Reuse the existing flow: persists the id + bootstraps /me with the Bearer token.
        await login(String(principal));
        navigate(user?.state?.returnTo || '/', { replace: true });
      } catch (err) {
        setError(err.message || 'Sign-in could not be completed.');
      }
    })();
  }, [login, navigate]);

  return (
    <div className="login-page">
      {error
        ? <ErrorState message={error} onRetry={() => navigate('/login', { replace: true })} />
        : <Spinner size="lg" label="Completing sign-in" />}
    </div>
  );
}

export default AuthCallback;
